import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

// Soft Modern modal. Backdrop, esc-to-close, body-scroll lock, rounded card,
// soft shadow. No focus trap (single-form modals don't need it).
export function Dialog({
  open,
  onClose,
  title,
  size = "lg",
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: "md" | "lg" | "xl";
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = {
    md: "max-w-[640px]",
    lg: "max-w-[900px]",
    xl: "max-w-[1180px]",
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-stretch justify-center bg-ink/30 px-6 py-8 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          "relative flex w-full flex-col overflow-hidden rounded-xl bg-surface shadow-lift",
          widths[size],
          className,
        )}
      >
        {title && (
          <header className="flex items-center justify-between gap-4 border-b border-rule px-6 py-4">
            <div className="text-[0.95rem] font-semibold text-ink">{title}</div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full text-[1.3rem] leading-none text-ink-muted transition-colors hover:bg-canvas-2 hover:text-ink"
            >
              ×
            </button>
          </header>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
